import React, { forwardRef } from 'react';

import DragHandlerIcon from '@atlaskit/icon/core/migration/drag-handle--drag-handler';
import { Pressable, type XCSS, xcss } from '@atlaskit/primitives';

import { type ListVariant } from '../../types';

import type { DragHandleButtonAppearance, DragHandleButtonProps } from './types';

const buttonStyles = xcss({
	borderRadius: 'border.radius.100',
	padding: 'space.0',
	width: 'max-content',
	border: 'none',
	cursor: 'grab',
	display: 'flex',
});

const spaceAppearances: Record<ListVariant, XCSS> = {
	rotation: xcss({
		marginLeft: 'space.050',
	}),
	participant: xcss({
		marginLeft: 'space.075',
	}),
};

const buttonAppearances: Record<DragHandleButtonAppearance, XCSS> = {
	default: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.neutral',
		':hover': {
			backgroundColor: 'color.background.neutral.hovered',
		},
		':active': {
			backgroundColor: 'color.background.neutral.pressed',
		},
	}),
	selected: xcss({
		color: 'color.text.selected',
		backgroundColor: 'color.background.selected',
		':hover': {
			backgroundColor: 'color.background.selected.hovered',
		},
		':active': {
			backgroundColor: 'color.background.selected.pressed',
		},
	}),
	disabled: xcss({
		color: 'color.text.disabled',
		backgroundColor: 'color.background.neutral',
		cursor: 'not-allowed',
	}),
};

export const DragHandleButton = forwardRef<HTMLButtonElement, DragHandleButtonProps>(
	({ label, variant, isSelected = false, isDisabled = false, ...buttonProps }, ref) => {
		const look = isSelected ? 'selected' : isDisabled ? 'disabled' : 'default';
		return (
			<Pressable
				ref={ref}
				xcss={[buttonStyles, buttonAppearances[look], spaceAppearances[variant]]}
				{...buttonProps}
			>
				<DragHandlerIcon color="currentColor" spacing="spacious" label={label} />
			</Pressable>
		);
	},
);
